import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SmartToy as AiIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { workflowService } from '../services/workflow.service';
import { Workflow } from '../types/workflow.types';
import { useAuth } from '../contexts/AuthContext';

export const WorkflowsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState<Workflow | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    loadWorkflows();
  }, [page, rowsPerPage]);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await workflowService.getWorkflows({
        page: page + 1,
        page_size: rowsPerPage,
      });
      setWorkflows(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load workflows');
      enqueueSnackbar('Failed to load workflows', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const hasPerm = (perm: string) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  };

  const canCreate = hasPerm('workflow.create');
  const canExecute = hasPerm('workflow.execute');
  const canDelete = hasPerm('workflow.delete');
  const canEdit = hasPerm('workflow.edit');
  const isSystemAdmin = !!user && user.role === 'system_admin';

  const handleExecute = async (workflow: Workflow) => {
    try {
      if (!canExecute) {
        enqueueSnackbar('You do not have permission to execute workflows.', { variant: 'error' });
        return;
      }
      const result = await workflowService.executeWorkflow(workflow.id);
      enqueueSnackbar(`Workflow execution started: ${result.execution_id}`, { variant: 'success' });
      navigate(`/app/executions`);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to execute workflow', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;

    try {
      if (!canDelete) {
        enqueueSnackbar('You do not have permission to delete workflows.', { variant: 'error' });
        return;
      }
      await workflowService.deleteWorkflow(workflowToDelete.id);
      enqueueSnackbar('Workflow deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setWorkflowToDelete(null);
      loadWorkflows();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to delete workflow', { variant: 'error' });
    }
  };

  const handleDuplicate = async (workflow: Workflow) => {
    try {
      if (!canCreate) {
        enqueueSnackbar('You do not have permission to create workflows.', { variant: 'error' });
        return;
      }
      await workflowService.duplicateWorkflow(workflow.id);
      enqueueSnackbar('Workflow duplicated successfully', { variant: 'success' });
      loadWorkflows();
      setAnchorEl(null);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to duplicate workflow', { variant: 'error' });
    }
  };

  const handleExport = async (workflow: Workflow) => {
    try {
      const data = await workflowService.exportWorkflow(workflow.id);
      // Build a friendly, filesystem-safe filename from the workflow name.
      const safeName = (workflow.name || 'workflow')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'workflow';
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = `${safeName}-${stamp}.json`;

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar(`Exported "${workflow.name}"`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(
        err.response?.data?.error || err.message || 'Failed to export workflow',
        { variant: 'error' }
      );
    } finally {
      handleMenuClose();
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, workflow: Workflow) => {
    setAnchorEl(event.currentTarget);
    setSelectedWorkflow(workflow);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedWorkflow(null);
  };

  const handleAiGenerate = async () => {
    const prompt = aiPrompt.trim();
    if (!prompt) return;

    try {
      setAiGenerating(true);
      setAiError(null);
      const result = await workflowService.aiGenerate({ prompt });
      enqueueSnackbar(result.explanation || 'Workflow generated successfully!', { variant: 'success' });
      setAiDialogOpen(false);
      setAiPrompt('');
      navigate(`/app/workflows/${result.workflow_id}`);
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Failed to generate workflow';
      setAiError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setAiGenerating(false);
    }
  };

  const getStatusChip = (workflow: Workflow) => {
    if (!workflow.is_active) {
      return <Chip label="Inactive" color="default" size="small" />;
    }
    if (!workflow.is_published) {
      return <Chip label="Unpublished" color="warning" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Workflows</Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AiIcon />}
            onClick={() => setAiDialogOpen(true)}
          >
            Generate with AI
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              if (!canCreate) {
                enqueueSnackbar('You do not have permission to create workflows.', { variant: 'error' });
                return;
              }
              navigate('/app/workflows/new');
            }}
            disabled={!canCreate}
          >
            Create Workflow
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Last Executed</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workflows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No workflows found. Create your first workflow to get started!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              workflows.map((workflow) => (
                <TableRow key={workflow.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {workflow.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                      {workflow.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(workflow)}</TableCell>
                  <TableCell>v{workflow.version}</TableCell>
                  <TableCell>
                    {workflow.last_executed_at
                      ? new Date(workflow.last_executed_at).toLocaleString()
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    {new Date(workflow.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleExecute(workflow)}
                      title="Execute"
                    >
                      <PlayIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (!canEdit) {
                          enqueueSnackbar('You do not have permission to edit workflows.', { variant: 'error' });
                          return;
                        }
                        navigate(`/app/workflows/${workflow.id}`);
                      }}
                      title="Edit"
                      disabled={!canEdit}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, workflow)}
                      title="More options"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedWorkflow) {
              handleDuplicate(selectedWorkflow);
            }
          }}
          disabled={!canCreate}
        >
          <CopyIcon fontSize="small" sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        {isSystemAdmin && (
          <MenuItem
            onClick={() => {
              if (selectedWorkflow) {
                handleExport(selectedWorkflow);
              }
            }}
          >
            <ExportIcon fontSize="small" sx={{ mr: 1 }} />
            Export JSON
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (selectedWorkflow) {
              setWorkflowToDelete(selectedWorkflow);
              setDeleteDialogOpen(true);
              handleMenuClose();
            }
          }}
          disabled={!canDelete}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Workflow</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{workflowToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiDialogOpen}
        onClose={() => { if (!aiGenerating) { setAiDialogOpen(false); setAiError(null); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon color="secondary" />
          Generate Workflow with AI
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe the workflow you want to create. The AI will generate the nodes and connections for you.
          </Typography>
          {aiError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAiError(null)}>
              {aiError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            placeholder={'e.g. "When a webhook is received, check if the status code is an error. If yes, send a Slack notification to #alerts. Otherwise, store the data in Google Sheets."'}
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            disabled={aiGenerating}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleAiGenerate();
              }
            }}
          />
          {aiGenerating && (
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Generating workflow... This may take a few seconds.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAiDialogOpen(false); setAiError(null); }} disabled={aiGenerating}>
            Cancel
          </Button>
          <Button
            onClick={handleAiGenerate}
            variant="contained"
            color="secondary"
            disabled={!aiPrompt.trim() || aiGenerating}
            startIcon={aiGenerating ? <CircularProgress size={16} color="inherit" /> : <AiIcon />}
          >
            {aiGenerating ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
